import { useEffect, useState } from "react";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import StatusBadge from "../../../components/identity-verification/StatusBadge";
import DocumentUploadField from "../../../components/identity-verification/DocumentUploadField";
import {
  getProfessionalIdentityVerification,
  submitProfessionalIdentityVerification,
  type ProfessionalIdentityVerification,
} from "../../../api/identityVerification";

export default function ProIdentityVerificationPage() {
  const { professional } = useProfessionalAuth();
  const nip = professional?.nip?.trim() ?? "";
  const [state, setState] = useState<ProfessionalIdentityVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nip) return;
    let mounted = true;
    setLoading(true);
    getProfessionalIdentityVerification(nip).then((res) => {
      if (!mounted) return;
      if (res.ok) setState(res.data);
      else setError(res.message);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [nip]);

  const submit = async (body: { carteCipUrl?: string; attestationOrdreUrl?: string }) => {
    if (!nip) return { ok: false as const, message: "Session invalide." };
    const res = await submitProfessionalIdentityVerification(nip, body);
    if (!res.ok) return { ok: false as const, message: res.message };
    setState(res.data);
    return { ok: true as const };
  };

  const missing =
    state && state.statut === "non_soumis"
      ? [!state.carteCipUrl && "carte CIP", !state.attestationOrdreUrl && "attestation d'inscription à l'Ordre"]
          .filter(Boolean)
          .join(" et ")
      : "";

  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Vérification d&apos;identité</h1>
      <p className="mt-2 text-sm text-gray-600">
        Soumettez votre carte CIP et votre attestation d&apos;inscription à l&apos;Ordre des Médecins.
      </p>

      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Documents justificatifs</CardTitle>
          {state && <StatusBadge status={state.statut} />}
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {error && !loading && <p className="text-sm text-red-600">{error}</p>}

          {!loading && state && (
            <div className="space-y-6">
              {state.statut === "rejetee" && state.motifRejet && (
                <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  <span className="font-semibold">Motif du rejet : </span>
                  {state.motifRejet}
                </div>
              )}
              {missing && (
                <p className="text-sm text-amber-700">Document(s) manquant(s) : {missing}.</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DocumentUploadField
                  label="Carte CIP"
                  folderName="identity-verification/professionnels"
                  filename={nip ? `carte_cip_${nip}` : undefined}
                  currentUrl={state.carteCipUrl}
                  readOnly={state.statut === "validee"}
                  onSubmitted={(url) => submit({ carteCipUrl: url })}
                />
                <DocumentUploadField
                  label="Attestation d'inscription à l'Ordre"
                  folderName="identity-verification/professionnels"
                  filename={nip ? `attestation_ordre_${nip}` : undefined}
                  currentUrl={state.attestationOrdreUrl}
                  readOnly={state.statut === "validee"}
                  onSubmitted={(url) => submit({ attestationOrdreUrl: url })}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
